import { useState, useEffect, useRef } from 'react'
import React from 'react'
import { useLanguage } from '@/contexts/LanguageContext'
import { useAuth } from '@/hooks/useAuth'
import { Link } from 'react-router-dom'
import { Menu, X as CloseIcon, Linkedin, Facebook, Mail, Phone, User, LogOut } from 'lucide-react'

function Header() {
  const { language } = useLanguage()
  const { isAuthenticated, logout } = useAuth()
  const isRTL = language === 'dr' || language === 'ps'
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const [resourcesOpen, setResourcesOpen] = useState(false)
  // Services dropdown accessibility state and refs
  const [servicesOpen, setServicesOpen] = useState(false)
  const [aboutOpen, setAboutOpen] = useState(false)
  const [projectsOpen, setProjectsOpen] = useState(false)
  const [whatWeDoOpen, setWhatWeDoOpen] = useState(false)
  const serviceItemsRef = useRef<(HTMLAnchorElement | null)[]>([])
  const servicesContainerRef = useRef<HTMLDivElement | null>(null)
  const servicesTriggerRef = useRef<HTMLButtonElement | null>(null)
  const aboutContainerRef = useRef<HTMLDivElement | null>(null)
  const projectsContainerRef = useRef<HTMLDivElement | null>(null)
  const whatWeDoContainerRef = useRef<HTMLDivElement | null>(null)
  const resourcesContainerRef = useRef<HTMLDivElement | null>(null)
  
  // Timeout refs for hover delays
  const servicesTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const resourcesTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const aboutTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const projectsTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  // const whatWeDoTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Hover delay functions for better UX - commented out as not currently used
  // const handleAboutMouseEnter = () => {
  //   if (aboutTimeoutRef.current) {
  //     clearTimeout(aboutTimeoutRef.current)
  //   }
  //   setAboutOpen(true)
  // }

  // const handleAboutMouseLeave = () => {
  //   aboutTimeoutRef.current = setTimeout(() => {
  //     setAboutOpen(false)
  //   }, 100) // 100ms delay
  // }

  // Hover delay functions for better UX
  const handleServicesMouseEnter = () => {
    if (servicesTimeoutRef.current) {
      clearTimeout(servicesTimeoutRef.current)
    }
    setServicesOpen(true)
  }

  const handleServicesMouseLeave = () => {
    servicesTimeoutRef.current = setTimeout(() => {
      setServicesOpen(false)
    }, 100) // 100ms delay
  }

  // const handleResourcesMouseEnter = () => {
  //   if (resourcesTimeoutRef.current) {
  //     clearTimeout(resourcesTimeoutRef.current)
  //   }
  //   setResourcesOpen(true)
  // }

  // const handleResourcesMouseLeave = () => {
  //   resourcesTimeoutRef.current = setTimeout(() => {
  //     setResourcesOpen(false)
  //   }, 100) // 100ms delay
  // }

  // const handleWhatWeDoMouseEnter = () => {
  //   if (whatWeDoTimeoutRef.current) {
  //     clearTimeout(whatWeDoTimeoutRef.current)
  //   }
  //   setWhatWeDoOpen(true)
  // }

  // const handleWhatWeDoMouseLeave = () => {
  //   whatWeDoTimeoutRef.current = setTimeout(() => {
  //     setWhatWeDoOpen(false)
  //   }, 100) // 100ms delay
  // }

  // Hardcoded categories and submenu items for Services

  function handleServicesTriggerKeyDown(e: React.KeyboardEvent<HTMLButtonElement>) {
    switch (e.key) {
      case 'ArrowDown':
      case 'Enter':
      case ' ': // Space
        e.preventDefault()
        setServicesOpen(true)
        // Focus first item on open
        requestAnimationFrame(() => serviceItemsRef.current[0]?.focus())
        break
      case 'Escape':
        setServicesOpen(false)
        break
    }
  }

  function handleServicesMenuKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    const items = serviceItemsRef.current.filter(Boolean) as HTMLAnchorElement[]
    if (!items.length) return
    const currentIndex = items.findIndex((el) => el === document.activeElement)

    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault()
        const next = items[(currentIndex + 1) % items.length]
        next?.focus()
        break
      }
      case 'ArrowUp': {
        e.preventDefault()
        const prev = items[(currentIndex - 1 + items.length) % items.length]
        prev?.focus()
        break
      }
      case 'Home':
        e.preventDefault()
        items[0]?.focus()
        break
      case 'End':
        e.preventDefault()
        items[items.length - 1]?.focus()
        break
      case 'Escape':
        setServicesOpen(false)
        break
    }
  }

  // Keyboard navigation handlers - currently unused but kept for future accessibility enhancements
  // function handleResourcesTriggerKeyDown(e: React.KeyboardEvent<HTMLButtonElement>) { ... }
  // function handleResourcesMenuKeyDown(e: React.KeyboardEvent<HTMLDivElement>) { ... }
  // function handleWhatWeDoTriggerKeyDown(e: React.KeyboardEvent<HTMLButtonElement>) { ... }
  // function handleWhatWeDoMenuKeyDown(e: React.KeyboardEvent<HTMLDivElement>) { ... }

  // Close on outside click
  useEffect(() => {
    function onDocMouseDown(ev: MouseEvent) {
      const servicesContainer = servicesContainerRef.current
      const resourcesContainer = resourcesContainerRef.current
      const aboutContainer = aboutContainerRef.current
      const projectsContainer = projectsContainerRef.current
      const whatWeDoContainer = whatWeDoContainerRef.current
      
      if (servicesContainer && !servicesContainer.contains(ev.target as Node)) {
        if (servicesOpen) setServicesOpen(false)
      }
      
      if (resourcesContainer && !resourcesContainer.contains(ev.target as Node)) {
        if (resourcesOpen) setResourcesOpen(false)
      }
      
      if (aboutContainer && !aboutContainer.contains(ev.target as Node)) {
        if (aboutOpen) setAboutOpen(false)
      }
      
      if (projectsContainer && !projectsContainer.contains(ev.target as Node)) {
        if (projectsOpen) setProjectsOpen(false)
      }
      
      if (whatWeDoContainer && !whatWeDoContainer.contains(ev.target as Node)) {
        if (whatWeDoOpen) setWhatWeDoOpen(false)
      }
    }
    document.addEventListener('mousedown', onDocMouseDown)
    return () => document.removeEventListener('mousedown', onDocMouseDown)
  }, [servicesOpen, resourcesOpen, aboutOpen, projectsOpen, whatWeDoOpen])

  // Close search on outside click
  useEffect(() => {
    function onSearchClickOutside(ev: MouseEvent) {
      const searchContainer = document.querySelector('[data-search-container]')
      if (!searchContainer) return
      if (!searchContainer.contains(ev.target as Node)) {
        setIsSearchOpen(false)
      }
    }
    if (isSearchOpen) {
      document.addEventListener('mousedown', onSearchClickOutside)
      return () => document.removeEventListener('mousedown', onSearchClickOutside)
    }
  }, [isSearchOpen])

  // Dynamic About data for header contact info - currently unused
  // const [about, setAbout] = React.useState<AboutType | null>(null)
  // React.useEffect(() => { ... }, [])

  // Cleanup timeouts on unmount
  React.useEffect(() => {
    return () => {
      if (servicesTimeoutRef.current) {
        clearTimeout(servicesTimeoutRef.current)
      }
      if (resourcesTimeoutRef.current) {
        clearTimeout(resourcesTimeoutRef.current)
      }
      if (aboutTimeoutRef.current) {
        clearTimeout(aboutTimeoutRef.current)
      }
      if (projectsTimeoutRef.current) {
        clearTimeout(projectsTimeoutRef.current)
      }
    }
  }, [])

  // Extract contact info from About data
  const email = 'info@dachauffeur.ca'
  const mobile = '+1 (234) 567-890'
  const logoUrl = null

  // Language selector - currently unused but kept for future i18n
  // const languages = [...]

  return (
    <header className="fixed top-0 left-0 right-0 z-50" dir={isRTL ? 'rtl' : 'ltr'}>
      <style>{`
        .dropdown-mega {
          position: fixed;
          left: 0;
          right: 0;
          width: 100%;
          top: 110px;
          background-color: rgba(0, 0, 0, 0.75);
          color: #ffffff;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12);
          display: flex;
          padding: 28px;
          gap: 20px;
          flex-wrap: wrap;
          z-index: 1001;
          backdrop-filter: blur(6px);
        }

        .dropdown-section {
          flex: 1;
          min-width: 250px;
          padding-left: 20px;
          border-left: 1px solid rgba(0, 0, 0, 0.2);
        }

        .dropdown-section:first-child {
          border-left: none;
          padding-left: 0;
        }

        .dropdown-section {
          border-left-color: rgba(255, 255, 255, 0.3);
        }

        .dropdown-section:not(:first-child) {
          border-left: 1px solid rgba(255, 255, 255, 0.3);
        }

        .dropdown-section h4 {
          font-size: 13px;
          font-weight: 800;
          color: #1774a7;
          margin: 0 0 12px 0;
          padding-bottom: 8px;
          color: #ffffff;
        }

        .dropdown-section h4:not(:first-child) {
          margin-top: 24px;
        }

        .dropdown-section ul {
          list-style: none;
          margin: 0;
          padding: 0;
        }

        .dropdown-section li {
          margin: 0;
          padding: 0;
        }

        .dropdown-section a {
          color: #ffffff;
          text-decoration: none;
          font-size: 12px;
          font-weight: 600;
          display: block;
          padding: 8px 0;
          transition: all 0.3s ease;
        }

        .dropdown-section a:hover {
          color: #1774a7;
          padding-left: 8px;
        }

        .dropdown-description-section {
          flex: 1;
          min-width: 250px;
          padding-left: 0;
          border-left: none;
        }

        .dropdown-description-text {
          margin-right: 100px;
        }

        .dropdown-description-text p {
          font-size: 12px;
          line-height: 1.6;
          color: #ffffff;
          margin: 0;
        }

        .dropdown-media {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          flex: 1;
          min-width: 250px;
          padding-left: 20px;
          border-left: 1px solid rgba(255, 255, 255, 0.3);
        }

        .dropdown-media figure {
          margin: 0;
          padding: 0;
          overflow: hidden;
          border-radius: 8px;
          width: 100%;
          max-width: 280px;
          height: auto;
        }

        .dropdown-media img {
          width: 100%;
          height: auto;
          display: block;
        }
      `}</style>
      {/* Top Bar */}
      <div className="bg-gradient-to-r from-primary via-secondary to-black text-white py-3 border-b border-gold-500/20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            {/* Contact Info */}
            <div className="flex flex-wrap items-center gap-6 text-sm">
              {/* Email */}
              <a href={`mailto:${email}`} className="flex items-center gap-2 hover:text-gold-400 transition-all duration-300 group">
                <Mail size={14} className="text-gold-400 group-hover:scale-110 transition-transform" />
                <span className="hidden sm:inline">{email}</span>
              </a>
              {/* Phone */}
              <a href={`tel:${mobile}`} className="flex items-center gap-2 hover:text-gold-400 transition-all duration-300 group">
                <Phone size={14} className="text-gold-400 group-hover:scale-110 transition-transform" />
                <span className="hidden sm:inline">{mobile}</span>
              </a>
              {/* 24/7 Service Badge */}
              <span className="bg-gold-500/20 border border-gold-500/40 px-3 py-1 rounded-full text-xs font-semibold text-gold-300 backdrop-blur-sm">
                24/7 Service
              </span>
            </div>
            
            {/* Social Media Icons */}
            <div className="flex items-center gap-3">
              <a href="https://www.linkedin.com/company/dachauffeur" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="w-8 h-8 bg-gold-500/20 hover:bg-gold-500 border border-gold-500/30 hover:border-gold-500 rounded-full flex items-center justify-center transition-all duration-300 group hover:scale-110">
                <Linkedin size={16} className="text-gold-400 group-hover:text-white transition-colors" />
              </a>
              <a href="https://www.facebook.com/dachauffeur" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="w-8 h-8 bg-gold-500/20 hover:bg-gold-500 border border-gold-500/30 hover:border-gold-500 rounded-full flex items-center justify-center transition-all duration-300 group hover:scale-110">
                <Facebook size={16} className="text-gold-400 group-hover:text-white transition-colors" />
              </a>
              <a href="https://www.instagram.com/dachauffeur" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="w-8 h-8 bg-gold-500/20 hover:bg-gold-500 border border-gold-500/30 hover:border-gold-500 rounded-full flex items-center justify-center transition-all duration-300 group hover:scale-110">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-gold-400 group-hover:text-white transition-colors">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.204 0 3.584-.012 4.849-.07 3.225-.148 4.771-1.691 4.919-4.919.058-1.265-.069-1.645-.069-4.849 0-3.204 0-3.584.012-4.849.07-3.26.149-4.771-1.699-4.919-4.92-.058-1.265-.069-1.644-.069-4.849 0-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 4.92-.058 1.265-.069 1.645-.069 4.849 0 3.204 0 3.584-.012 4.849-.07 3.225-.148 4.771-1.691 4.919-4.919.058-1.265-.069-1.645-.069-4.849 0-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 4.92zM5.838 12a6.162 6.162 0 1112.324 0 6.162 6.162 0 01-12.324 0zM12 16a4 4 0 110-8 4 4 0 010 8zm4.965-10.405a1.44 1.44 0 112.881.001 1.44 1.44 0 01-2.881-.001z" fill="currentColor"/>
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>
      {/* Main Header */}
      <div className="bg-gradient-to-br from-white/95 via-white/90 to-gray-50/95 backdrop-blur-md border-b border-gold-500/20 shadow-xl sticky top-0 z-50">
        <div className="w-full max-w-7xl mx-auto px-4 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            {logoUrl ? (
              <img src={logoUrl} alt="DaChauffeur" className="h-14 w-auto" />
            ) : (
              <div className="h-14 flex items-center justify-center">
                <span className="text-2xl font-serif font-bold text-primary">DaChauffeur</span>
              </div>
            )}
          </Link>
          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1" aria-label="Primary">
            {/* Services Dropdown */}
            <div
              className="relative group"
              ref={servicesContainerRef}
              onMouseEnter={handleServicesMouseEnter}
              onMouseLeave={handleServicesMouseLeave}
            >
              <button
                ref={servicesTriggerRef}
                type="button"
                className="relative text-gray-700 hover:text-primary px-5 py-3 text-sm font-semibold transition-all duration-300 group hover:bg-gold-500/10 rounded-lg flex items-center gap-1"
                aria-haspopup="menu"
                aria-expanded={servicesOpen}
                aria-controls="services-menu"
                onClick={() => setServicesOpen((v) => !v)}
                onKeyDown={(e) => handleServicesTriggerKeyDown(e)}
              >
                Services
                <svg className="w-4 h-4 transition-transform duration-300 group-hover:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <div className={`${servicesOpen ? 'block' : 'hidden'}`} onMouseEnter={handleServicesMouseEnter} onMouseLeave={handleServicesMouseLeave}>
                <div
                  id="services-menu"
                  role="menu"
                  aria-label="Services"
                  className="dropdown-mega"
                  onKeyDown={(e) => handleServicesMenuKeyDown(e)}
                >
                  <div className="dropdown-section dropdown-description-section">
                    <h4>Premium Transportation Services</h4>
                    <div className="dropdown-description-text">
                      <p>Experience luxury transportation with our professional chauffeur services. From airport transfers to special events, we ensure comfort, punctuality, and elegance.</p>
                    </div>
                  </div>
                  <div className="dropdown-section">
                    <h4>Our Services</h4>
                    <ul>
                      <li><Link to="/services/airport-transfers" ref={el => (serviceItemsRef.current[0] = el)} role="menuitem" tabIndex={-1} onClick={() => setServicesOpen(false)}>Airport Transfers</Link></li>
                      <li><Link to="/services/corporate" ref={el => (serviceItemsRef.current[1] = el)} role="menuitem" tabIndex={-1} onClick={() => setServicesOpen(false)}> Corporate Travel</Link></li>
                      <li><Link to="/services/weddings" ref={el => (serviceItemsRef.current[2] = el)} role="menuitem" tabIndex={-1} onClick={() => setServicesOpen(false)}>Weddings & Events</Link></li>
                      <li><Link to="/services/hourly" ref={el => (serviceItemsRef.current[3] = el)} role="menuitem" tabIndex={-1} onClick={() => setServicesOpen(false)}>Hourly Service</Link></li>
                      <li><Link to="/services/point-to-point" ref={el => (serviceItemsRef.current[4] = el)} role="menuitem" tabIndex={-1} onClick={() => setServicesOpen(false)}>Point-to-Point</Link></li>
                    </ul>
                  </div>
                  <div className="dropdown-section">
                    <h4>Specialized Services</h4>
                    <ul>
                      <li><Link to="/services/city-tours" ref={el => (serviceItemsRef.current[5] = el)} role="menuitem" tabIndex={-1} onClick={() => setServicesOpen(false)}>City Tours</Link></li>
                      <li><Link to="/services/special-occasions" ref={el => (serviceItemsRef.current[6] = el)} role="menuitem" tabIndex={-1} onClick={() => setServicesOpen(false)}>Special Occasions</Link></li>
                      <li><Link to="/services/road-shows" ref={el => (serviceItemsRef.current[7] = el)} role="menuitem" tabIndex={-1} onClick={() => setServicesOpen(false)}>Road Shows</Link></li>
                      <li><Link to="/services/executive-protection" ref={el => (serviceItemsRef.current[8] = el)} role="menuitem" tabIndex={-1} onClick={() => setServicesOpen(false)}>Executive Protection</Link></li>
                    </ul>
                  </div>
                  <div className="dropdown-media">
                    <figure>
                      <img alt="Luxury Transportation Services" src="https://images.unsplash.com/photo-1550355291-bbee04a92027?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80" />
                    </figure>
                    <div className="dropdown-description-text">
                      <p>Professional chauffeurs, luxury vehicles, and exceptional service for all your transportation needs.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Fleet */}
            <Link to="/fleet" className="relative text-gray-700 hover:text-primary px-5 py-3 text-sm font-semibold transition-all duration-300 group hover:bg-gold-500/10 rounded-lg">
              Fleet
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gold-500 transition-all duration-300 group-hover:w-full"></span>
            </Link>
            
            {/* Pricing */}
            <Link to="/pricing" className="relative text-gray-700 hover:text-primary px-5 py-3 text-sm font-semibold transition-all duration-300 group hover:bg-gold-500/10 rounded-lg">
              Pricing
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gold-500 transition-all duration-300 group-hover:w-full"></span>
            </Link>
            
            {/* Book Now - Prominent CTA */}
            <Link 
              to="/booking" 
              className="bg-gradient-to-r from-gold-500 to-gold-600 text-primary px-6 py-3 text-sm font-bold rounded-lg hover:from-gold-600 hover:to-gold-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 border border-gold-500/30"
            >
              Book Now
            </Link>
            
            {/* Login / My Account Button */}
            {isAuthenticated ? (
              <div className="relative group">
                <button className="flex items-center gap-2 text-gray-700 hover:text-primary px-4 py-2 text-sm font-semibold transition-all duration-300 hover:bg-gold-500/10 rounded-lg">
                  <User size={16} />
                  <span>My Account</span>
                </button>
                {/* Dropdown Menu */}
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  <div className="py-2">
                    <Link 
                      to="/dashboard" 
                      className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gold-500/10 hover:text-primary transition-colors"
                    >
                      <User size={16} />
                      Dashboard
                    </Link>
                    <button 
                      onClick={logout}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-red-500/10 hover:text-red-600 transition-colors"
                    >
                      <LogOut size={16} />
                      Logout
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <Link 
                to="/auth/login" 
                className="text-gray-700 hover:text-primary px-4 py-2 text-sm font-semibold transition-all duration-300 hover:bg-gold-500/10 rounded-lg"
              >
                Login
              </Link>
            )}
            
            {/* About Us */}
            <Link to="/about" className="relative text-gray-700 hover:text-primary px-5 py-3 text-sm font-semibold transition-all duration-300 group hover:bg-gold-500/10 rounded-lg">
              About Us
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gold-500 transition-all duration-300 group-hover:w-full"></span>
            </Link>
            
            {/* Contact */}
            <Link to="/contact" className="relative text-gray-700 hover:text-primary px-5 py-3 text-sm font-semibold transition-all duration-300 group hover:bg-gold-500/10 rounded-lg">
              Contact
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gold-500 transition-all duration-300 group-hover:w-full"></span>
            </Link>
          </nav>

          {/* Mobile Menu Button */}
          <button 
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} 
            className="lg:hidden bg-gradient-to-r from-gold-500 to-gold-600 text-primary p-3 rounded-lg hover:from-gold-600 hover:to-gold-700 transition-all duration-300 shadow-lg"
          >
            {isMobileMenuOpen ? <CloseIcon size={24} /> : <Menu size={24} />}
          </button>
        </div>
      
      {/* Mobile Navigation */}
      {isMobileMenuOpen && (
        <div className="bg-gradient-to-br from-white/95 via-white/90 to-gray-50/95 backdrop-blur-md text-primary lg:hidden border-t border-gold-500/20 shadow-2xl">
          <div className="max-w-7xl mx-auto px-4 py-6 space-y-3">
            {/* Services */}
            <div className="px-4 py-2">
              <h4 className="text-sm font-semibold text-gold-500 mb-3">Services</h4>
              <div className="space-y-2 pl-4">
                <Link to="/services/airport-transfers" className="block py-2 text-sm text-gray-600 hover:text-primary hover:bg-gold-500/10 rounded transition-all duration-300">Airport Transfers</Link>
                <Link to="/services/corporate" className="block py-2 text-sm text-gray-600 hover:text-primary hover:bg-gold-500/10 rounded transition-all duration-300">Corporate Travel</Link>
                <Link to="/services/weddings" className="block py-2 text-sm text-gray-600 hover:text-primary hover:bg-gold-500/10 rounded transition-all duration-300">Weddings & Events</Link>
                <Link to="/services/hourly" className="block py-2 text-sm text-gray-600 hover:text-primary hover:bg-gold-500/10 rounded transition-all duration-300">Hourly Service</Link>
                <Link to="/services/point-to-point" className="block py-2 text-sm text-gray-600 hover:text-primary hover:bg-gold-500/10 rounded transition-all duration-300">Point-to-Point</Link>
                <Link to="/services/special-occasions" className="block py-2 text-sm text-gray-600 hover:text-primary hover:bg-gold-500/10 rounded transition-all duration-300">Special Occasions</Link>
              </div>
            </div>
            
            <Link to="/fleet" className="block px-4 py-3 text-sm font-semibold text-gray-700 hover:text-primary hover:bg-gold-500/10 rounded-lg transition-all duration-300">Fleet</Link>
            
            <Link to="/pricing" className="block px-4 py-3 text-sm font-semibold text-gray-700 hover:text-primary hover:bg-gold-500/10 rounded-lg transition-all duration-300">Pricing</Link>
            
            <Link to="/booking" className="block mx-4 mt-4 bg-gradient-to-r from-gold-500 to-gold-600 text-primary px-6 py-3 text-sm font-bold rounded-lg hover:from-gold-600 hover:to-gold-700 transition-all duration-300 shadow-lg text-center">Book Now</Link>
            
            {/* Login / My Account Button */}
            {isAuthenticated ? (
              <div className="px-4 py-2 border-t border-gray-200 mt-4">
                <div className="text-sm font-semibold text-gold-500 mb-3">My Account</div>
                <Link to="/dashboard" className="flex items-center gap-2 py-2 text-sm text-gray-700 hover:text-primary hover:bg-gold-500/10 rounded transition-all duration-300">
                  <User size={16} />
                  Dashboard
                </Link>
                <button 
                  onClick={logout}
                  className="w-full flex items-center gap-2 py-2 text-sm text-gray-700 hover:bg-red-500/10 hover:text-red-600 rounded transition-all duration-300"
                >
                  <LogOut size={16} />
                  Logout
                </button>
              </div>
            ) : (
              <Link to="/auth/login" className="block mx-4 mt-2 text-center py-2 text-sm font-semibold text-gray-700 hover:text-primary hover:bg-gold-500/10 rounded-lg transition-all duration-300">Login</Link>
            )}
            
            <Link to="/about" className="block px-4 py-3 text-sm font-semibold text-gray-700 hover:text-primary hover:bg-gold-500/10 rounded-lg transition-all duration-300">About Us</Link>
            
            <Link to="/contact" className="block px-4 py-3 text-sm font-semibold text-gray-700 hover:text-primary hover:bg-gold-500/10 rounded-lg transition-all duration-300">Contact</Link>
          </div>
        </div>
      )}
      </div>
    </header>
  )
}

export default Header